import { useParams } from 'react-router-dom';
import Button from 'src/components/Button';
import Tabs from 'src/components/Tabs';
import { IconSpinner } from 'src/icons';
import { getMonthName } from 'src/logic/getMonthName';
import { routes } from 'src/routes';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';

import { useConfirmRelatedServicesMutation, useInvoiceDetailsQuery } from '../billing/billingApi';
import { BillingHeader } from '../billing/BillingHeader';
import BillingSignatureModal from '../billing/BillingSignatureModal';
import { setInvoicesIdsForSignature } from '../billing/billingSlice';
import { useSingleStudentDetailsQuery } from '../single-student/singleStudentApi';
import { EmptyServicesState } from './EmptyServicesState';
import { RelatedServiceList } from './RelatedServiceList';

export const LogServices = () => {
  const { id } = useParams();
  const { data: invoiceData, isLoading: isInvoiceLoading } = useInvoiceDetailsQuery(id!, {
    skip: !id,
  });
  const dispatch = useAppDispatch();
  const [confirmRelatedServices, { isLoading: isConfirming }] = useConfirmRelatedServicesMutation();
  const relatedServices = invoiceData?.relatedServices;
  const canConfirm =
    relatedServices?.some((service) => !service.isConfirmed) &&
    relatedServices.every((service) => service.status);

  const countFilledServices = relatedServices?.filter((service) => service.status).length;

  const tabs = [
    {
      label: 'Related Services',
      id: 'related-services',
      to: `${routes.billing.root}/${routes.billing.logServices.root.replace(':id', id || '')}`,
      count: countFilledServices || 0,
      isSelected: true,
    },
  ];

  const { data: student } = useSingleStudentDetailsQuery(invoiceData?.studentId || '', {
    skip: !invoiceData?.studentId,
  });

  const onFinalizeBilling = () => {
    dispatch(setInvoicesIdsForSignature([id!]));
  };

  const handleConfirmAll = async () => {
    if (!relatedServices?.length || !id) return;

    try {
      await confirmRelatedServices({
        id,
        payload: {
          placementRelatedServices: relatedServices,
        },
      }).unwrap();

      dispatch(addToast({ text: 'All related services have been confirmed', type: 'positive' }));
    } catch (error) {
      dispatch(
        addToast({
          text: 'Failed to confirm related services. Please try again later.',
          type: 'negative',
        }),
      );
      console.error('Failed to confirm related services:', error);
    }
  };

  if (isInvoiceLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <IconSpinner className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col bg-neutral-white">
      <BillingHeader onFinalize={onFinalizeBilling} />
      <Tabs items={tabs} underline className="px-4 sm:px-10" />
      <div className="flex h-full grow justify-center gap-12 bg-neutral-950 p-4 py-8 md:p-6">
        <div className="flex w-full flex-col">
          <div className="flex justify-between sm:px-4">
            <div className="flex flex-col gap-1">
              <p className=" text-heading-xl-mobile text-neutral-200 ">
                {invoiceData?.year &&
                  invoiceData.month &&
                  `${getMonthName(invoiceData.month)} ${invoiceData.year}`}
              </p>
            </div>
            <Button
              preset="secondary"
              onClick={handleConfirmAll}
              isLoading={isConfirming}
              disabled={!canConfirm}
            >
              Confirm all
            </Button>
          </div>
          {relatedServices?.length ? (
            <div className="pb-6 pt-9 sm:px-4">
              <RelatedServiceList relatedServices={relatedServices} invoiceData={invoiceData} />
            </div>
          ) : (
            <EmptyServicesState />
          )}
        </div>
      </div>
      <BillingSignatureModal />
    </div>
  );
};
